import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import {
  communityHref,
  leaderboardHref,
  ownShownIdentity,
  tierLabel,
  type OwnShownIdentity,
} from 'lib/community/model'
import { newDiscussionHref } from 'lib/community/discussions'
import {
  keepGoingLine,
  rewardLead,
  rewardPointsLine,
  rewardView,
  threadHref,
  type CommunityPointsTable,
  type CommunityPublishStandingNext,
} from 'lib/community/review'
import type { CommunityStanding } from 'lib/community/types'
import { newTabReturnLine } from 'lib/community/writer-handoff'
import type { OwnerClaimResponse, OwnerPublishResponse } from 'lib/dealhunt/types'
import type { OwnerTrack } from 'lib/owner-notes/analytics'
import { sharedMe } from 'lib/owner-notes/api'
import { makeItCountItems } from 'lib/owner-notes/band-copy'
import { AskOwners, MakeItCount } from 'components/owner-notes/AcceptResultExtras'
import CoachingLines from 'components/owner-notes/CoachingLines'
import ReviewPhotos from 'components/owner-notes/ReviewPhotos'
import { PHOTOS_COPY } from 'lib/owner-notes/review-photos'
import type { ReviewProduct } from './ReviewComposer'
import TierAvatar from './TierAvatar'
import PostClaimInvite from './PostClaimInvite'
import badges from './Badges.module.css'
import base from './community.module.css'
import styles from './Reward.module.css'

interface RewardScreenProps {
  product: ReviewProduct
  /** An `accept` with its `community` block (the composer shows this screen only then). */
  result: OwnerPublishResponse
  /** `/me`'s standing before the publish. */
  before: CommunityStanding | null
  /** A sign-in linked the profile — on Publish, or from this screen's "Make it count". */
  claim: OwnerClaimResponse | null
  /** The review is linked to an account (a claim, or `/me`'s `profile.claimed`) — live, so a
   *  claim from this screen swaps "Make it count" for the question box, as the band does. */
  linked: boolean
  /** `askOwnersSeed` from the writer's answers at publish (never the sentence). */
  seed: string
  /** The page's `community_page` tracker: "Make it count" reports `claim_started{publish}`. */
  trackOwner: OwnerTrack
  /** The claim from "Make it count" (the composer's `onClaimed`: `/me` and `claim`). */
  onClaimed: (result: OwnerClaimResponse) => void
  /** Public photos went with this publish (the "Photo +15" fallback for an older DealHunt). */
  photo: boolean
  /** The reader's photos did not attach to this publish: say so, with what to do. */
  photosDropped?: boolean
  points: CommunityPointsTable | null
  /** Where "Back …" leads and what it says (`writerBack`: the handoff's `from`, else community). */
  back: { href: string; label: string }
  /** Opened in a new tab (`?tab=new`): the origin tab already shows the review. */
  newTab?: boolean
}

type CopyState = 'idle' | 'copied' | 'failed'

/**
 * The reward screen (design 5c): it replaces the form after an accepted publish. Everything is
 * DealHunt's — the publish answer's `community` block (points, tier, founding number) and `/me`
 * re-read right after it for this month's rank, which the publish answer does not carry. `/me`
 * is uncached, so the rank is already right while the public pages catch up.
 *
 * What the band said after a publish and this screen keeps: the recognition (Constraint
 * 32354c7e — the one coaching slot a result shows), the account link; and the review's photos
 * with the "Photo +15" line (contract-review-photos §4 — paid at publish, nothing pending). No
 * promised email (Constraint 4095b005).
 *
 * The band's accept-result boxes (2026-10-09, `AcceptResultExtras`): an author whose review is
 * not linked gets "Make it count" — sign in → claim → `onClaimed`, never a link by itself
 * (#81, a20ec9b1) — and a linked one "Ask the other owners something", seeded from their answers.
 * Every figure in them is DealHunt's (`makeItCountItems` reads the publish answer).
 */
const RewardScreen: React.VFC<RewardScreenProps> = ({
  product,
  result,
  before,
  claim,
  linked,
  seed,
  trackOwner,
  onClaimed,
  photo,
  photosDropped = false,
  points,
  back,
  newTab = false,
}) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  const earned = result.community as CommunityPublishStandingNext
  const [after, setAfter] = useState<{
    standing: CommunityStanding | null
    published: number
    /** The author's own name and photo (contract B.3), for the hero disc. */
    shown: OwnShownIdentity | null
  }>()
  const [copy, setCopy] = useState<CopyState>('idle')
  const copyTimer = useRef<number>()

  useEffect(() => () => window.clearTimeout(copyTimer.current), [])

  // Read again after a claim from this screen: membership and this month's rank change with it.
  useEffect(() => {
    let live = true
    // Every write through `api.ts call()` forgot the shared `/me`: this is a fresh read.
    sharedMe().then(
      me => {
        if (live) {
          setAfter({
            standing: me.community ?? null,
            published: me.shelf.filter(s => s.note.status === 'published').length,
            shown: ownShownIdentity(me),
          })
        }
      },
      () => undefined
    )
    return () => {
      live = false
    }
  }, [claim])

  // A claim's standing is newer than the publish answer's (it may have made the author a member).
  const standing =
    claim?.community && claim.community.member !== earned.member
      ? { ...earned, member: claim.community.member }
      : earned
  const view = rewardView(standing, before, after?.standing ?? null, points, linked)
  const askHref =
    linked && product.slug
      ? newDiscussionHref({ kind: 'question', product: product.slug, title: seed }, trailingSlash)
      : null
  const thread = product.slug ? threadHref(product.slug, result.note.id, trailingSlash) : null
  const pointsLine = rewardPointsLine(earned, photo, points)

  const copyLink = (): void => {
    if (!thread) {
      return
    }
    const url = `${publicConfig('appUrl') ?? window.location.origin}${thread}`
    window.clearTimeout(copyTimer.current)
    const done = (state: CopyState): void => {
      setCopy(state)
      copyTimer.current = window.setTimeout(() => setCopy('idle'), 2500)
    }
    if (!navigator.clipboard) {
      done('failed')
      return
    }
    navigator.clipboard.writeText(url).then(
      () => done('copied'),
      () => done('failed')
    )
  }

  let copyLabel = 'Copy link'
  if (copy === 'copied') {
    copyLabel = 'Copied'
  } else if (copy === 'failed') {
    copyLabel = 'Couldn’t copy'
  }

  return (
    <div className={styles.reward} aria-live="polite">
      <section className={styles.hero} aria-labelledby="reward-title">
        {view.badge.kind === 'founding' ? (
          <span className={clsx(badges.hex, badges.badge_ink, styles.hex)}>
            <span className={clsx(badges.hexFill, styles.hexFill)}>
              <span className={styles.hexLabel}>{'Early'}</span>
              {`#${view.badge.number}`}
            </span>
          </span>
        ) : (
          <TierAvatar
            handle={claim?.handle ?? result.note.handle ?? 'you'}
            tier={view.badge.tier}
            size="xl"
            className={styles.avatar}
            src={after?.shown?.avatarUrl}
            name={after?.shown?.name}
          />
        )}
        <div className={styles.heroCopy}>
          <p className={styles.eyebrow}>
            {view.badge.kind === 'tier' ? tierLabel(view.badge.tier) : 'Early Owner'}
          </p>
          <h1 id="reward-title" className={styles.title}>
            {view.title}
          </h1>
          <p className={styles.lead}>{rewardLead(product.name, thread !== null)}</p>
          {newTab && <p className={styles.lead}>{newTabReturnLine(back.label)}</p>}
          {claim && <p className={styles.fine}>{`Linked to your account as @${claim.handle}.`}</p>}
        </div>
      </section>

      <ul className={styles.tiles}>
        {view.tiles.map(tile => (
          <li key={tile.id} className={clsx(base.card, styles.tile)}>
            <p className={base.eyebrow}>{tile.label}</p>
            <p className={styles.tileValue}>{tile.value}</p>
            {tile.detail && <p className={styles.tileDetail}>{tile.detail}</p>}
            {tile.id === 'month' && (
              <Linked href={leaderboardHref(trailingSlash)} className={base.cardLink}>
                {'Leaderboard →'}
              </Linked>
            )}
          </li>
        ))}
      </ul>

      <ReviewPhotos note={result.note} productName={product.name} className={styles.photos} />
      {(pointsLine || photosDropped) && (
        <div className={styles.notes}>
          {pointsLine && <p className={base.text}>{pointsLine}</p>}
          {photosDropped && <p className={base.text}>{PHOTOS_COPY.dropped}</p>}
        </div>
      )}
      <CoachingLines coaching={result.verdict.coaching} show="recognition" />
      {/* After a claim (at Publish or from Make it count): the way into the conversation. */}
      {claim && <PostClaimInvite thread={thread} />}
      {!linked && (
        <MakeItCount
          items={makeItCountItems(result.community)}
          trackOwner={trackOwner}
          retry="link"
          onClaimed={onClaimed}
        />
      )}

      <div className={styles.actions}>
        {thread && (
          <Linked href={thread} className={clsx(base.btn, base.btnLarge)}>
            {'See your thread'}
          </Linked>
        )}
        {thread && (
          <button type="button" className={styles.secondary} onClick={copyLink}>
            {copyLabel}
          </button>
        )}
        <Linked href={back.href} className={styles.secondary}>
          {back.label}
        </Linked>
        <span className={styles.srOnly} role="status">
          {copy === 'copied' ? 'Link copied' : ''}
        </span>
      </div>
      {askHref && <AskOwners seed={seed} href={askHref} />}

      <section className={clsx(base.card, styles.keep)} aria-labelledby="reward-keep-title">
        <p id="reward-keep-title" className={base.eyebrow}>
          {'Keep going'}
        </p>
        <p className={base.text}>
          {keepGoingLine(after ? after.published : null, Boolean(after?.standing?.member))}
        </p>
        <Linked href={communityHref(trailingSlash)} className={base.cardLink}>
          {'Pick another product →'}
        </Linked>
      </section>
    </div>
  )
}

export default RewardScreen
